// src/admin/AdminTravelCalendar.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
// import currencyService from '../services/currencyService';

const AdminTravelCalendar = ({ selectedJourneyId = null }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [travelData, setTravelData] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [allJourneys, setAllJourneys] = useState([]);
  const [filteredJourneys, setFilteredJourneys] = useState([]);

  // Load all journeys for admin view
  useEffect(() => {
    const loadAllJourneys = async () => {
      try {
        const journeysSnapshot = await getDocs(collection(db, 'journeys'));
        const journeysData = journeysSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setAllJourneys(journeysData);
        
        // If specific journey selected, filter to just that one
        if (selectedJourneyId) {
          const selectedJourney = journeysData.find(j => j.id === selectedJourneyId);
          setFilteredJourneys(selectedJourney ? [selectedJourney] : []);
        } else {
          // Show all paid journeys
          setFilteredJourneys(journeysData.filter(journey => journey.paymentStatus));
        }
      } catch (error) {
        console.error('Error loading journeys:', error);
      }
    };

    loadAllJourneys();
  }, [selectedJourneyId]);

  // Load travel data from filtered journeys
  useEffect(() => {
    const loadTravelData = async () => {
      if (!filteredJourneys.length) {
        setTravelData([]);
        setLoading(false);
        return;
      }

      try {
        const travelPeriods = [];

        for (const journey of filteredJourneys) {
          // Extract travel dates and allocations
          if (journey.startDate && journey.endDate) {
            const startDate = new Date(journey.startDate);
            const endDate = new Date(journey.endDate);
            
            // Create daily entries for this journey
            let dailyEntries = [];
            
            if (journey.dailyAllocations && journey.dailyAllocations.length > 0) {
              // Use existing daily allocations if available
              dailyEntries = journey.dailyAllocations.map((allocation, index) => {
                const currentDate = new Date(startDate);
                currentDate.setDate(startDate.getDate() + index);
                
                return {
                  date: currentDate.toISOString().split('T')[0],
                  journeyId: journey.id,
                  journeyName: journey.name || journey.code,
                  journeyCode: journey.code,
                  dailyAllowance: allocation.dailyAllowance || 0,
                  isActive: true,
                  routeName: journey.routeName || 'Unknown Route',
                  userEmail: journey.userEmail || 'No email',
                  paymentStatus: journey.paymentStatus || false,
                  startDate: journey.startDate,
                  endDate: journey.endDate
                };
              });
            } else {
              // Create entries from start/end dates if no dailyAllocations - show as error
              const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1; // Include both start and end dates
              
              for (let i = 0; i < daysDiff; i++) {
                const currentDate = new Date(startDate);
                currentDate.setDate(startDate.getDate() + i);
                
                dailyEntries.push({
                  date: currentDate.toISOString().split('T')[0],
                  journeyId: journey.id,
                  journeyName: journey.name || journey.code,
                  journeyCode: journey.code,
                  dailyAllowance: 0,
                  isActive: true,
                  routeName: journey.routeName || 'Unknown Route',
                  userEmail: journey.userEmail || 'No email',
                  paymentStatus: journey.paymentStatus || false,
                  startDate: journey.startDate,
                  endDate: journey.endDate,
                  isError: true // Flag to indicate this is missing allocation data
                });
              }
            }
            
            travelPeriods.push(...dailyEntries);
          }
        }

        // Sort by date
        travelPeriods.sort((a, b) => new Date(a.date) - new Date(b.date));
        setTravelData(travelPeriods);
      } catch (error) {
        console.error('Error loading travel data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTravelData();
  }, [filteredJourneys]);

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty days for previous month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Get travel data for a specific date
  const getTravelDataForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return travelData.filter(data => data.date === dateStr);
  };

  // Navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentMonth);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <p>Loading travel calendar...</p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: 0 }}>
          {selectedJourneyId ? 'Journey Travel Calendar' : 'All User Travel Calendar'}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div>
            <strong>{filteredJourneys.length}</strong> active journeys
          </div>
          <select 
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
      }}>
        <button
          onClick={() => navigateMonth(-1)}
          style={{
            padding: '8px 12px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Previous
        </button>
        
        <h4 style={{ margin: 0 }}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h4>
        
        <button
          onClick={() => navigateMonth(1)}
          style={{
            padding: '8px 12px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Next →
        </button>
      </div>

      {/* Calendar Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '2px',
        marginBottom: '10px'
      }}>
        {/* Header row */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{
            padding: '8px',
            fontWeight: 'bold',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            border: '1px solid #ddd'
          }}>
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day, index) => {
          if (!day) {
            return <div key={index} style={{ padding: '20px' }}></div>;
          }

          const dayTravelData = getTravelDataForDate(day);
          const isToday = day.toDateString() === new Date().toDateString();
          const totalAllowance = dayTravelData.reduce((sum, data) => sum + data.dailyAllowance, 0);
          
          let cellStyle = {
            padding: '4px',
            minHeight: '80px',
            border: '1px solid #ddd',
            backgroundColor: '#fff',
            position: 'relative',
            fontSize: '11px',
            overflow: 'hidden'
          };

          if (isToday) {
            cellStyle.backgroundColor = '#fff3cd';
            cellStyle.border = '2px solid #ffc107';
          }

          if (dayTravelData.length > 0) {
            cellStyle.backgroundColor = '#e3f2fd';
            cellStyle.border = '1px solid #2196f3';
          }

          return (
            <div key={day.getTime()} style={cellStyle}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {day.getDate()}
              </div>
              
              {dayTravelData.length > 0 && (
                <div style={{ fontSize: '9px' }}>
                  <div style={{
                    backgroundColor: '#2196f3',
                    color: 'white',
                    padding: '1px 3px',
                    borderRadius: '2px',
                    marginBottom: '2px',
                    fontWeight: 'bold'
                  }}>
                    {dayTravelData.length} user{dayTravelData.length > 1 ? 's' : ''}
                  </div>
                  
                  <div style={{
                    color: '#006400',
                    fontWeight: 'bold',
                    marginBottom: '2px'
                  }}>
                    Total: ${totalAllowance}
                  </div>
                  
                  {/* Show first few journey codes */}
                  {dayTravelData.slice(0, 2).map((data, i) => (
                    <div key={i} style={{
                      backgroundColor: data.isError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(0, 123, 255, 0.1)',
                      padding: '1px 2px',
                      borderRadius: '2px',
                      marginBottom: '1px',
                      fontSize: '8px',
                      color: data.isError ? '#721c24' : '#0066cc'
                    }}>
                      {data.journeyCode}: {data.isError ? '$0 (Error)' : `$${data.dailyAllowance}`}
                    </div>
                  ))}
                  
                  {dayTravelData.length > 2 && (
                    <div style={{
                      fontSize: '8px',
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      +{dayTravelData.length - 2} more...
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '20px',
        fontSize: '12px',
        marginTop: '15px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{
            width: '15px',
            height: '15px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3'
          }}></div>
          <span>Active Travel Days</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{
            width: '15px',
            height: '15px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107'
          }}></div>
          <span>Today</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{
            width: '15px',
            height: '15px',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            border: '1px solid #dc3545'
          }}></div>
          <span>Missing Allocations (Error)</span>
        </div>
      </div>

      {/* Summary Stats */}
      {travelData.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px'
        }}>
          <h4>Summary Statistics</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <strong>Total Active Journeys:</strong> {filteredJourneys.length}
            </div>
            <div>
              <strong>Total Travel Days:</strong> {travelData.length}
            </div>
            <div>
              <strong>Total Daily Allowances:</strong> ${travelData.reduce((sum, data) => sum + data.dailyAllowance, 0)}
            </div>
            <div>
              <strong>Average Daily Allowance:</strong> ${Math.round(travelData.reduce((sum, data) => sum + data.dailyAllowance, 0) / travelData.length)}
            </div>
          </div>
        </div>
      )}

      {travelData.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666',
          fontStyle: 'italic'
        }}>
          No active travel data found for the selected criteria.
        </div>
      )}
    </div>
  );
};

export default AdminTravelCalendar;